import { useFormContext } from "react-hook-form";
import InputField from "@medoma/components/InputField/InputField";
import Checkbox from "@/components/Checkbox/Checkbox";
import styles from "./PatientIdentifierInput.module.scss";
import { useUserProfile } from "@/api/Users";
import { primaryPatientIdentifierType } from "@medoma/models/organizations";
import { personalIdentityNumberTypeSchema } from "@medoma/models/patients";
import { useFeatureFlag, knownFeatureFlagsSchema } from "@/api/FeatureFlags";
import { useLingui } from "@lingui/react/macro";

const swedishIdentityNumberRegex = /^\d{8}(?:-?\d{4})$/;
const eMedicusNumberRegex = /^\d{6}$/;
const swissPatientNumberRegex = /^\d{7,10}$/;
const latvianPersonasKodsRegex = /^(?:\d{11}|\d{6}-\d{5})$/;

export type PatientIdentifierInputFields = {
  identifier: string;
  isTemporaryNumber: boolean;
};

const PatientIdentifierInput = ({ readonly }: { readonly?: boolean }) => {
  const { t } = useLingui();
  const {
    register,
    formState: { errors },
    watch,
    clearErrors,
  } = useFormContext<PatientIdentifierInputFields>();

  const { data: temporaryNumberEnabled } = useFeatureFlag(
    knownFeatureFlagsSchema.enum.SwedishTemporaryIdentityNumber,
  );
  const { organization } = useUserProfile();
  const isTemporaryNumber = watch("isTemporaryNumber");
  const primaryIdentifierType = primaryPatientIdentifierType(organization);

  const getValidationPattern = () => {
    if (isTemporaryNumber) {
      return undefined;
    }

    switch (primaryIdentifierType) {
      case personalIdentityNumberTypeSchema.enum.SwedishPersonalIdentityNumber:
        return {
          value: swedishIdentityNumberRegex,
          message: t`Ange personnummer med 12 siffror med eller utan bindestreck`,
        };
      case personalIdentityNumberTypeSchema.enum.SwissEMedicusNumber:
        return {
          value: eMedicusNumberRegex,
          message: t`Ange e-Medicus-nummer med 6 siffror`,
        };
      case personalIdentityNumberTypeSchema.enum.SwissPatientNumber:
        return {
          value: swissPatientNumberRegex,
          message: t`Ange patientnummer med 7-10 siffror`,
        };
      case personalIdentityNumberTypeSchema.enum.LatvianPersonasKods:
        return {
          value: latvianPersonasKodsRegex,
          message: t`Ange personas kods med 11 siffror eller 6 siffror, bindestreck och 5 siffror`,
        };
      default:
        throw primaryIdentifierType satisfies never;
    }
  };

  const getRequiredMessage = () => {
    if (isTemporaryNumber) {
      return t`Ange reservnummer`;
    }

    switch (primaryIdentifierType) {
      case personalIdentityNumberTypeSchema.enum.SwedishPersonalIdentityNumber:
        return t`Ange personnummer`;
      case personalIdentityNumberTypeSchema.enum.SwissEMedicusNumber:
        return t`Ange e-Medicus-nummer`;
      case personalIdentityNumberTypeSchema.enum.SwissPatientNumber:
        return t`Ange patientnummer`;
      case personalIdentityNumberTypeSchema.enum.LatvianPersonasKods:
        return t`Ange personas kods`;
      default:
        throw primaryIdentifierType satisfies never;
    }
  };

  const getLabel = () => {
    if (isTemporaryNumber) {
      return t`Reservnummer`;
    }

    switch (primaryIdentifierType) {
      case personalIdentityNumberTypeSchema.enum.SwedishPersonalIdentityNumber:
        return t`Personnummer`;
      case personalIdentityNumberTypeSchema.enum.SwissEMedicusNumber:
        return t`e-Medicus-nummer`;
      case personalIdentityNumberTypeSchema.enum.SwissPatientNumber:
        return t`Patientnummer`;
      case personalIdentityNumberTypeSchema.enum.LatvianPersonasKods:
        return t`Personas kods`;
      default:
        throw primaryIdentifierType satisfies never;
    }
  };

  return (
    <div className={styles.patientIdentifierInput}>
      <InputField
        readOnly={readonly}
        type="text"
        {...register("identifier", {
          required: {
            value: true,
            message: getRequiredMessage(),
          },
          pattern: getValidationPattern(),
        })}
        label={getLabel()}
        showOptionalLabel={false}
        description={readonly ? t`Går inte att ändra` : undefined}
        errorMessage={errors.identifier?.message}
      />

      {/* Nothing to choose once the identifier is settled: the label above already reads
          "Reservnummer" when it is one. */}
      {temporaryNumberEnabled && !readonly ? (
        <Checkbox
          label={{ text: t`Reservnummer` }}
          {...register("isTemporaryNumber", {
            onChange: () => clearErrors("identifier"),
          })}
        />
      ) : null}
    </div>
  );
};

export default PatientIdentifierInput;
